import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { useMemo } from 'react';
import type { PostHarmonicBackgroundLevelGetHarmonicBackgroundLevelValuesApiResponse } from '@redux/apiSlices/harmonicBackgroundLevelApi';
import type { ApiCommonResponsesBaseCrudResponse601ApiCommonExternalDtoMeasurementParameterExternalDto } from '@redux/apiSlices/measurementParameterApi';
import type { XAxisPlotBandsOptions } from 'highcharts/highcharts.src';

export type harmonicsBoxplotProps = {
  adaptedData: number[][];
  categories: string[];
  tsoId: number;
  measurementDeviceLabel: string;
  plotBands?: XAxisPlotBandsOptions[];
};

export type BoxplotDataPoint = {
  x: number;
  low: number | null;
  q1: number | null;
  median: number | null;
  q3: number | null;
  high: number | null;
  color: string;
  fillColor: string;
  order: string;
  phase: number;
};

const fillColorMap = [
  tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled_lt,
  tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_hover_lt,
  tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_pressed_lt,
];

export const SortHarmonicDataByOrderThenPhase = (
  harmonicData: PostHarmonicBackgroundLevelGetHarmonicBackgroundLevelValuesApiResponse,
  measurementParameters: ApiCommonResponsesBaseCrudResponse601ApiCommonExternalDtoMeasurementParameterExternalDto
) => {
  if (!measurementParameters?.externalDtos) return harmonicData;
  const sortedData = [...harmonicData];
  sortedData.sort((a, b) => {
    const measurementParameterA = measurementParameters.externalDtos?.find((x) => x.id === a.measurementParameterId);
    const measurementParameterB = measurementParameters.externalDtos?.find((x) => x.id === b.measurementParameterId);

    if (!measurementParameterA || !measurementParameterB) return 0;

    const orderA = Number.parseInt(measurementParameterA.categoryPath.split('order')[1] || '0');
    const orderB = Number.parseInt(measurementParameterB.categoryPath.split('order')[1] || '0');

    const orderCompare = orderA - orderB;
    if (orderCompare !== 0) return orderCompare;

    return measurementParameterA.technicalName.localeCompare(measurementParameterB.technicalName); //sort by phase
  });

  return sortedData;
};

export const useFilteredBoxplotData = (adaptedData: number[][], categories: string[], showOnlyWorstPhase: boolean) => {
  const color = tsoPqTheme.palette.customTextColor.color_font_ft_nrtrl_disabled;

  return useMemo(() => {
    const filteredData: BoxplotDataPoint[] = [];
    const labels: Record<number, string> = {};
    const tickPositions: number[] = [];

    if (adaptedData.length === 0) {
      return { filteredData: [], labels: {}, tickPositions: [] };
    }

    adaptedData.forEach((data, index) => {
      if (data.length === 0) return;

      if (showOnlyWorstPhase) {
        if (index % 3 === 0) {
          const phases = adaptedData.slice(index, index + 3);
          const q95Index = 3;
          const quantiles = phases.map((x) => x[q95Index]);
          const worstValue = Math.max(...quantiles);
          const worstPhase = phases.find((x) => x[q95Index] === worstValue) ?? [null, null, null, null, null];
          const [low, q1, median, q3, high] = worstPhase;

          const phaseIndex = index % 3;

          const groupIndex = Math.floor(index / 3);
          const pointPlacement = groupIndex * 0.5;

          const boxplotDatapoint: BoxplotDataPoint = {
            x: pointPlacement,
            low,
            q1,
            median,
            q3,
            high,
            color,
            fillColor: fillColorMap[phaseIndex],
            order: categories[index],
            phase: phaseIndex + 1,
          };

          filteredData.push(boxplotDatapoint);

          labels[pointPlacement] = categories[index] || `Order ${index + 1} `;
          tickPositions.push(pointPlacement);
        }
      } else {
        const [low, q1, median, q3, high] = data;

        const groupIndex = Math.floor(index / 3);
        const phaseIndex = index % 3;
        const basePosition = groupIndex * 2;
        const pointPlacement = basePosition + phaseIndex * 0.5;

        const boxplotDataPoint: BoxplotDataPoint = {
          x: pointPlacement,
          low,
          q1,
          median,
          q3,
          high,
          color,
          fillColor: fillColorMap[phaseIndex],
          order: categories[index],
          phase: phaseIndex + 1,
        };

        filteredData.push(boxplotDataPoint);

        if (phaseIndex === 1) {
          labels[pointPlacement] = categories[index] || `Order ${index + 1} `;
          tickPositions.push(pointPlacement);
        }
      }
    });

    return { filteredData, labels, tickPositions };
  }, [adaptedData, categories, color, showOnlyWorstPhase]);
};
